import React, { useEffect, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  DollarSign,
  Clock,
  CheckCircle,
  Target,
  Users,
  FileText,
  Quote,
  ExternalLink,
  Download,
  Link2,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/api";
import projectDefaultImg from "../../image/Default Project.png";
import SEO from "../SEO";

// Array of 20 distinct project colors for dynamic design matching
const projectColors = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#f97316",
  "#22c55e",
  "#a855f7",
  "#14b8a6",
  "#eab308",
  "#d946ef",
  "#84cc16",
  "#0ea5e9",
  "#fb923c",
  "#6366f1",
  "#db2777",
  "#065f46",
  "#be123c",
];

const getProjectColor = (project, fallbackIndex = 0) => {
  if (project?.color) return project.color;
  const hash = project?.id
    ? project.id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
    : fallbackIndex;
  return projectColors[hash % projectColors.length];
};

const PortfolioDetail = () => {
  const { slug } = useParams();

  // Fetch exclusively from your server API layer
  const {
    data: project,
    isLoading, // Changed from isFetching to prevent background sync unmounting
    isError,
  } = useQuery({
    queryKey: ["project", slug],
    queryFn: () => {
      return api.get(`/api/projects/public/${slug}`).then((res) => res.data);
    },
  });

  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    if (!project) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.2 },
      );

      gsap.fromTo(
        contentRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.4 },
      );

      if (sidebarRef.current) {
        gsap.fromTo(
          sidebarRef.current,
          { x: 30, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.6 },
        );
      }
    });

    return () => ctx.revert();
  }, [project]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isError || (!project && !isLoading)) {
    return <Navigate to="/projects" replace />;
  }

  const activeColor = getProjectColor(project);

  // Parse server timelines (Format: YYYY-MM)
  const formatTimeline = () => {
    if (!project.start_date) return "Recent";
    const startYear = project.start_date.split("-")[0];
    if (project.end_date) {
      const endYear = project.end_date.split("-")[0];
      return startYear === endYear ? startYear : `${startYear} - ${endYear}`;
    }
    return `${startYear} - Present`;
  };

  const projectTimeline = formatTimeline();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <>
      <SEO
        title={project.title}
        description={project.description}
        keywords={project.tags}
        url={`https://agoraconsulting-et.com/projects/${project.slug}`}
        image={`${backendUrl}${project.image}`}
      />
      <main className="relative overflow-hidden bg-white">
        {/* Background Graphic Layer */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)",
            }}
          />
          <div
            className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full"
            style={{
              background: `radial-gradient(circle, ${activeColor}15 0%, transparent 70%)`,
              filter: "blur(80px)",
            }}
          />
        </div>

        <div className="container-fluid relative z-10 pt-32 pb-20 px-4 md:px-8 max-w-7xl mx-auto">
          <div className="max-w-6xl mx-auto">
            {/* Back Action Button */}
            <div className="mb-8">
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors duration-300 font-medium text-sm"
              >
                <ArrowLeft size={16} />
                <span>Back to Portfolio</span>
              </Link>
            </div>

            {/* Project Header */}
            <header ref={headerRef} className="mb-12" style={{ opacity: 0 }}>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase px-3 py-1.5 rounded-full border"
                  style={{
                    color: activeColor,
                    background: `${activeColor}15`,
                    borderColor: `${activeColor}30`,
                  }}
                >
                  {project.category || "General"}
                </span>
                {project.status && (
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase px-3 py-1.5 rounded-full ${
                      project.status.toLowerCase() === "completed"
                        ? "bg-green-50 text-green-700 border border-green-200"
                        : "bg-blue-50 text-blue-700 border border-blue-200"
                    }`}
                  >
                    <CheckCircle size={10} />
                    {project.status}
                  </span>
                )}
              </div>

              <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-4 font-display">
                {project.title}
              </h1>

              {/* Client, Summary, & Meta Indicators */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600 mb-6">
                {project.client && (
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-slate-400" />
                    <span className="font-medium">Client:</span>
                    <span className="font-semibold text-slate-900">
                      {project.client}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-slate-400" />
                  <span>{projectTimeline}</span>
                </div>
              </div>

              {/* Main Featured Image Container */}
              {project.image && (
                <div className="relative rounded-3xl overflow-hidden mb-8 border border-slate-200 aspect-[21/9] bg-slate-100 shadow-sm">
                  <img
                    src={
                      project.image
                        ? `${backendUrl}${project.image}`
                        : projectDefaultImg
                    }
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Rich HTML Content Rendering Section */}
              {project.description && (
                <div
                  dangerouslySetInnerHTML={{ __html: project.description }}
                  className="text-lg bg-white p-4 rounded-2xl text-slate-700 leading-relaxed max-w-4xl font-light project-rich-text mb-6"
                />
              )}
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 items-start">
              {/* Server Data: Main Body Lists */}
              <article
                ref={contentRef}
                className="lg:col-span-3 space-y-12"
                style={{ opacity: 0 }}
              >
                {/* Challenges Section */}
                {project.extended_data?.challenges &&
                  project.extended_data.challenges.length > 0 && (
                    <section className="rounded-2xl p-4">
                      <h2 className="text-2xl font-bold text-slate-900 mb-4 font-display">
                        The Challenges
                      </h2>
                      <div className="space-y-3">
                        {project.extended_data.challenges.map(
                          (challenge, i) => (
                            <p
                              key={i}
                              className="text-slate-700 leading-relaxed font-light"
                            >
                              {challenge}
                            </p>
                          ),
                        )}
                      </div>
                    </section>
                  )}

                {/* Approach Section */}
                {project.extended_data?.approach &&
                  project.extended_data.approach.length > 0 && (
                    <section className="rounded-2xl p-4">
                      <h2 className="text-2xl font-bold text-slate-900 mb-4 font-display">
                        Our Approach
                      </h2>
                      <ul className="space-y-3">
                        {project.extended_data.approach.map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle
                              size={16}
                              className="text-emerald-500 mt-1 flex-shrink-0"
                            />
                            <span className="text-slate-700 font-light">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                {/* Outcomes Section */}
                {project.extended_data?.outcomes &&
                  project.extended_data.outcomes.length > 0 && (
                    <section className="rounded-2xl p-4">
                      <h2 className="text-2xl font-bold text-slate-900 mb-4 font-display">
                        Key Outcomes
                      </h2>
                      <ul className="space-y-3">
                        {project.extended_data.outcomes.map((outcome, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                              style={{ background: `${activeColor}15` }}
                            >
                              <span
                                className="text-xs font-bold"
                                style={{ color: activeColor }}
                              >
                                {i + 1}
                              </span>
                            </div>
                            <span className="text-slate-700 font-light">
                              {outcome}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                {/* Impact Section */}
                {project.extended_data?.impact &&
                  project.extended_data.impact.length > 0 && (
                    <section className="rounded-3xl p-8 border border-slate-200 bg-slate-50/50 relative overflow-hidden">
                      <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2 font-display">
                        <Target size={20} style={{ color: activeColor }} />
                        Impact & Results
                      </h2>
                      <div className="space-y-3">
                        {project.extended_data.impact.map((impactItem, i) => (
                          <p
                            key={i}
                            className="text-slate-700 leading-relaxed font-light"
                          >
                            {impactItem}
                          </p>
                        ))}
                      </div>
                    </section>
                  )}

                {/* Methodology Section */}
                {project.extended_data?.methodology &&
                  project.extended_data.methodology.length > 0 && (
                    <section className="rounded-2xl p-4">
                      <h2 className="text-2xl font-bold text-slate-900 mb-4 font-display">
                        Methodology
                      </h2>
                      <ul className="space-y-3">
                        {project.extended_data.methodology.map((method, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <FileText
                              size={16}
                              className="text-slate-400 mt-1 flex-shrink-0"
                            />
                            <span className="text-slate-700 font-light">
                              {method}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                {/* Deliverables Grid Layout Section */}
                {project.extended_data?.deliverables &&
                  project.extended_data.deliverables.length > 0 && (
                    <section className="rounded-2xl p-4">
                      <h2 className="text-2xl font-bold text-slate-900 mb-4 font-display">
                        Key Deliverables
                      </h2>
                      <div className="grid md:grid-cols-2 gap-4">
                        {project.extended_data.deliverables.map(
                          (deliverable, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm"
                            >
                              <Download
                                size={16}
                                style={{ color: activeColor }}
                                className="mt-1 flex-shrink-0"
                              />
                              <span className="text-slate-700 text-sm font-light">
                                {deliverable}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </section>
                  )}

                {/* Testimonial Section */}
                {project.extended_data?.testimonial?.content && (
                  <section className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm relative">
                    <Quote
                      size={48}
                      className="text-slate-100 absolute top-4 right-6 pointer-events-none"
                    />
                    <div className="flex items-start gap-4 relative z-10">
                      <Quote
                        size={24}
                        style={{ color: activeColor }}
                        className="flex-shrink-0 transform rotate-180"
                      />
                      <div>
                        <blockquote className="text-lg text-slate-700 italic mb-4 leading-relaxed font-light">
                          "{project.extended_data.testimonial.content}"
                        </blockquote>
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">
                            {project.extended_data.testimonial.author ||
                              "Anonymous Partner"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>
                )}
              </article>

              {/* Meta Context Sidebar Panels */}
              <aside
                ref={sidebarRef}
                className="lg:col-span-1"
                style={{ opacity: 0 }}
              >
                <div className="sticky top-24 space-y-6">
                  {/* Core Specifications */}
                  <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
                    <h3 className="font-bold text-slate-900 border-b border-slate-100 pb-2 font-display">
                      Project Specs
                    </h3>

                    {project.location && (
                      <div className="flex items-start gap-3">
                        <MapPin
                          size={16}
                          style={{ color: activeColor }}
                          className="mt-0.5 flex-shrink-0"
                        />
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                            Location
                          </p>
                          <p className="text-sm text-slate-900 font-medium">
                            {project.location}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3">
                      <DollarSign
                        size={16}
                        className={`${project.budget ? "text-emerald-500" : "text-slate-300"} mt-0.5 flex-shrink-0`}
                      />
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                          Budget
                        </p>
                        <p className="text-sm text-slate-900 font-medium">
                          {project.budget || "Confidential"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Calendar
                        size={16}
                        className="text-slate-400 mt-0.5 flex-shrink-0"
                      />
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
                          Timeline
                        </p>
                        <p className="text-sm text-slate-900 font-medium">
                          {projectTimeline}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Metrics Section */}
                  {project.extended_data?.metrics &&
                    project.extended_data.metrics.length > 0 && (
                      <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-4">
                        <h3 className="font-bold text-slate-900 border-b border-slate-100 pb-2 font-display">
                          Metrics
                        </h3>
                        <div className="space-y-3">
                          {project.extended_data.metrics.map((metric, i) => (
                            <div
                              key={i}
                              className="text-center p-4 bg-slate-50 rounded-2xl border border-slate-100"
                            >
                              <div className="text-2xl font-mono font-bold text-slate-900 mb-0.5">
                                {metric.value}
                              </div>
                              <div className="text-[10px] text-slate-500 font-bold tracking-wider uppercase leading-tight">
                                {metric.label}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* External Action Links Module */}
                  {(project.project_link || project.pdfFile) && (
                    <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-3">
                      <h3 className="font-bold text-slate-900 border-b border-slate-100 pb-2 font-display">
                        Resources
                      </h3>
                      {project.project_link && (
                        <a
                          href={project.project_link}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between text-xs font-bold text-slate-700 hover:text-slate-900 border border-slate-200 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                        >
                          <span className="inline-flex items-center gap-2">
                            <Link2 size={14} /> Launch Site
                          </span>
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {project.pdfFile && (
                        <a
                          href={project.pdfFile}
                          download
                          className="flex items-center justify-between text-xs font-bold text-slate-700 hover:text-slate-900 border border-slate-200 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                        >
                          <span className="inline-flex items-center gap-2">
                            <FileText size={14} /> Case Summary PDF
                          </span>
                          <Download size={12} />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Focus Areas Cloud */}
                  {project.tags && project.tags.length > 0 && (
                    <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm space-y-3">
                      <h3 className="font-bold text-slate-900 border-b border-slate-100 pb-2 font-display">
                        Focus Areas
                      </h3>
                      <div className="flex flex-wrap gap-1.5">
                        {project.tags.map((tag, i) => (
                          <span
                            key={i}
                            className="text-[10px] font-bold tracking-wider uppercase bg-slate-50 text-slate-600 border border-slate-200 px-2.5 py-1 rounded-md"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default PortfolioDetail;
